/**
 * An invite's auto-updated copy, item by item (decision 75; part 38b log, DI5): what the writer's device seals for an
 * invitee and the invitee's device (part 38c) opens. Two kinds: a shared chapter (its title as the preview shows it, its
 * source), one item per chapter whoever holds it; and an invite's manifest (the book's metadata and theme as part 05
 * serialises them, and the invite's chapters in the book's order), one per invite, so no invitee learns of a chapter it
 * wasn't given; part 38e (table MP) adds the places of the shared notes that invite reads on its chapters, so a passage's
 * place follows the text without a note sent again. Each is UTF-8 JSON of exactly its fields; `readInviteCopy` refuses anything else, field by field: the
 * invitee takes it from a relay it doesn't trust (Definition of Done, security). Pure.
 */
import { MAX_CHAPTERS } from '../import/chapters.ts';

/** What every item says it is. */
export const INVITE_COPY_FORMAT = 'cockatrice-invite-copy';

/** The version written. */
export const INVITE_COPY_VERSION = 1;

/** A shared chapter's copy. */
export interface InviteChapterCopy {
  readonly kind: 'chapter';
  readonly title: string;
  readonly text: string;
}

/** The most places a manifest names (part 38e, MP5). */
export const MAX_MANIFEST_PLACES = 10_000;

/** Part 38e, table MP: where a shared note's passage is in its chapter's copy, by the note's ID at the relay. */
export interface ManifestPlace {
  /** The note's ID at the relay (32 lowercase hex digits). */
  readonly note: string;
  /** Its passage's range in the chapter's copy; null when the passage is gone (MP4: the whole chapter). */
  readonly range: { readonly from: number; readonly to: number } | null;
}

/** An invite's manifest: the book's look and its chapters for that invite, in order, and its notes' places. */
export interface InviteManifest {
  readonly kind: 'manifest';
  /** Part 05's `serializeMetadata` output. */
  readonly metadata: string;
  /** Part 05's `serializeTheme` output. */
  readonly theme: string;
  /** The chapters' IDs (32 lowercase hex digits), each once. */
  readonly chapters: readonly string[];
  /** Part 38e, MP1: the places of the shared notes this invite reads on its chapters, each note once. */
  readonly places: readonly ManifestPlace[];
}

/** An item of an invite's copy. */
export type InviteCopy = InviteChapterCopy | InviteManifest;

/** Thrown by {@link readInviteCopy} for bytes that aren't an item; `field` says where. */
export class InviteCopyError extends Error {
  override readonly name = 'InviteCopyError';
  readonly field: string;

  constructor(field: string, problem: string) {
    super(`${field === '' ? 'the copy' : field}: ${problem}`);
    this.field = field;
  }
}

const UTF8 = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true });
const ID = /^[0-9a-f]{32}$/u;
const CHAPTER_KEYS = ['format', 'version', 'kind', 'title', 'text'];
const MANIFEST_KEYS = ['format', 'version', 'kind', 'metadata', 'theme', 'chapters', 'places'];
const PLACE_KEYS = ['note', 'range'];
const RANGE_KEYS = ['from', 'to'];

/** The item's bytes. */
export function encodeInviteCopy(copy: InviteCopy): Uint8Array<ArrayBuffer> {
  const head = { format: INVITE_COPY_FORMAT, version: INVITE_COPY_VERSION };
  const value =
    copy.kind === 'chapter'
      ? { ...head, kind: 'chapter', title: copy.title, text: copy.text }
      : { ...head, kind: 'manifest', metadata: copy.metadata, theme: copy.theme, chapters: [...copy.chapters], places: copy.places.map(({ note, range }) => ({ note, range: range === null ? null : { from: range.from, to: range.to } })) };
  return new TextEncoder().encode(JSON.stringify(value));
}

/** The own fields of object `value`, holding exactly `keys`. */
function fieldsOf(value: object, keys: readonly string[]): ReadonlyMap<string, unknown> {
  const fields = new Map(Object.entries(value));
  for (const key of fields.keys()) if (!keys.includes(key)) throw new InviteCopyError('', `has an unknown field "${key.slice(0, 64)}"`);
  for (const key of keys) if (!fields.has(key)) throw new InviteCopyError(key, 'is missing');
  return fields;
}

function textOf(fields: ReadonlyMap<string, unknown>, key: string): string {
  const value = fields.get(key);
  if (typeof value !== 'string') throw new InviteCopyError(key, 'must be text');
  return value;
}

/** The object `value` is and the kind it names, read before its fields are. */
function kindOf(value: unknown): { readonly kind: 'chapter' | 'manifest'; readonly object: object } {
  const kind: unknown = typeof value === 'object' && value !== null && !Array.isArray(value) ? Reflect.get(value, 'kind') : undefined;
  if ((kind !== 'chapter' && kind !== 'manifest') || typeof value !== 'object' || value === null) throw new InviteCopyError('kind', 'must be "chapter" or "manifest"');
  return { kind, object: value };
}

/** An offset into a chapter's text: a whole number from 0 below 2^31. */
function offsetOf(fields: ReadonlyMap<string, unknown>, key: string, at: string): number {
  const value = fields.get(key);
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0 || value >= 2 ** 31) throw new InviteCopyError(`${at}.${key}`, 'must be a whole number from 0 below 2^31');
  return value + 0;
}

/** MP1, MP4, MP5: a manifest's places: at most {@link MAX_MANIFEST_PLACES}, each a note's ID once and a range from ≤ to, or null. */
function placesOf(raw: unknown): readonly ManifestPlace[] {
  if (!Array.isArray(raw) || raw.length > MAX_MANIFEST_PLACES) throw new InviteCopyError('places', `must be a list of at most ${String(MAX_MANIFEST_PLACES)}`);
  const places = raw.map((value: unknown, index) => {
    const at = `places[${String(index)}]`;
    if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new InviteCopyError(at, 'must be a place');
    const fields = new Map<string, unknown>(Object.entries(value));
    if (fields.size !== PLACE_KEYS.length || !PLACE_KEYS.every((key) => fields.has(key))) throw new InviteCopyError(at, 'must hold exactly note and range');
    const note: unknown = fields.get('note');
    if (typeof note !== 'string' || !ID.test(note)) throw new InviteCopyError(`${at}.note`, 'must be 32 lowercase hex digits');
    const raw: unknown = fields.get('range');
    if (raw === null) return { note, range: null };
    if (typeof raw !== 'object' || Array.isArray(raw)) throw new InviteCopyError(`${at}.range`, 'must be a range or null');
    const range = new Map<string, unknown>(Object.entries(raw));
    if (range.size !== RANGE_KEYS.length || !RANGE_KEYS.every((key) => range.has(key))) throw new InviteCopyError(`${at}.range`, 'must hold exactly from and to');
    const from = offsetOf(range, 'from', `${at}.range`);
    const to = offsetOf(range, 'to', `${at}.range`);
    if (to < from) throw new InviteCopyError(`${at}.range`, 'must not end before it starts');
    return { note, range: { from, to } };
  });
  if (new Set(places.map((place) => place.note)).size !== places.length) throw new InviteCopyError('places', 'must not name a note twice');
  return places;
}

/**
 * The item `bytes` hold; throws {@link InviteCopyError} for anything else: bytes that aren't strict UTF-8 JSON of exactly
 * one kind's fields, another format or version, or a manifest whose chapters aren't 1 to {@link MAX_CHAPTERS} IDs of
 * the form, none twice, or whose places aren't as {@link placesOf} reads them.
 */
export function readInviteCopy(bytes: Uint8Array): InviteCopy {
  let value: unknown;
  try {
    value = JSON.parse(UTF8.decode(bytes));
  } catch {
    throw new InviteCopyError('', 'must be UTF-8 JSON');
  }
  const { kind, object } = kindOf(value);
  const fields = fieldsOf(object, kind === 'chapter' ? CHAPTER_KEYS : MANIFEST_KEYS);
  if (fields.get('format') !== INVITE_COPY_FORMAT) throw new InviteCopyError('format', `must be "${INVITE_COPY_FORMAT}"`);
  if (fields.get('version') !== INVITE_COPY_VERSION) throw new InviteCopyError('version', `must be ${String(INVITE_COPY_VERSION)}`);
  if (kind === 'chapter') return { kind, title: textOf(fields, 'title'), text: textOf(fields, 'text') };
  const raw = fields.get('chapters');
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > MAX_CHAPTERS) throw new InviteCopyError('chapters', `must be a list of 1 to ${String(MAX_CHAPTERS)}`);
  const chapters = raw.map((id: unknown, index) => {
    if (typeof id !== 'string' || !ID.test(id)) throw new InviteCopyError(`chapters[${String(index)}]`, 'must be 32 lowercase hex digits');
    return id;
  });
  if (new Set(chapters).size !== chapters.length) throw new InviteCopyError('chapters', 'must not hold an ID twice');
  return { kind, metadata: textOf(fields, 'metadata'), theme: textOf(fields, 'theme'), chapters, places: placesOf(fields.get('places')) };
}
